import { useMemo } from 'react';
import lodashGroupBy from 'lodash-es/groupBy';
import { umUimCoverageArray } from '@ourbranch/lookups';
import { resolveCarTotalFromRates } from '@ourbranch/policy-utils';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import { currencyFormatter } from 'core/helpers/formatters';

const carsFieldTypeMapper = {
  COLL: 'deductibleCollision',
  COMP: 'deductibleComprehensive',
  TRNSP: 'limitRental',
  ROAD: 'roadsideAssistance',
  LOAN: 'coverageLoan',
  UMPD: 'limitUMPD'
};

const otherFieldTypeMapper = {
  per_trailer_total: 'trailerTotal',
  renters_total: 'rentersTotal'
};

const initialPremiumAutoCoverage = {
  policyLimitBIPD: 0,
  policyLimitMedicalPayments: 0,
  policyLimitUMBI: 0,
  policyLimitPIP: 0,
  policyLimitUMPD: 0,
  policyLimitUIMBI: 0
};

const getUmpd = (umpd) => (['UMUIMPD', 'UMPD'].includes(umpd) ? 'policyLimitUMPD' : 'policyLimitUMBI');

// implementation similar to quote-fe/src/review/auto-policy
const globalAutoLimits = (state) => {
  const limits = {
    BI: 'policyLimitBIPD',
    PD: 'policyLimitBIPD',
    OPS_EXP: 'policyLimitBIPD',
    ACQ_EXP: 'policyLimitBIPD',
    MED: 'policyLimitMedicalPayments',
    PIP: 'policyLimitPIP',
    UM_UIM: 'policyLimitUIMBI',
    UM: 'policyLimitUMBI',
    UIM: 'policyLimitUIMBI',
    UMUIMBI: 'policyLimitUMBI',
    UMUIMPD: 'policyLimitUMPD'
  };

  return (umUimCoverageArray[state] || []).reduce((acc, uim) => ({ ...acc, [uim]: getUmpd(uim) }), limits);
};

const accumulatePremiumAutoCoverage = (rates, autoLimits, premiumCoverage = initialPremiumAutoCoverage) =>
  rates.reduce(
    (acc, rate) => ({ ...acc, [autoLimits[rate.type]]: acc[autoLimits[rate.type]] + rate.amount }),
    premiumCoverage
  );

const getCarPremiumBreakdown = (
  rates,
  premiumBreakdown,
  initialBreakdown,
  { excludeUmUim = false, umUimTypes } = {}
) => {
  const mappedRatesConstant = rates.reduce((breakdown, rate) => {
    if (carsFieldTypeMapper[rate.type]) {
      return { ...breakdown, [carsFieldTypeMapper[rate.type]]: currencyFormatter(rate.amount) };
    }

    return breakdown;
  }, initialBreakdown);

  let vehiclePriceAmount;
  if (premiumBreakdown) {
    // Shared with the account Price Breakdown so the two cannot drift:
    // PER_CAR_TOTAL when stored, otherwise the legacy line-item sum minus double-counted UM/UIM
    vehiclePriceAmount = resolveCarTotalFromRates(rates, { excludeUmUim, umUimTypes });
  } else {
    vehiclePriceAmount = rates.filter((r) => r.type === 'per_car_total').find((r) => r.amount)?.amount || 0;
  }

  return { ...mappedRatesConstant, vehiclePrice: currencyFormatter(vehiclePriceAmount) };
};

const getOtherPremiumBreakdown = (rates) =>
  rates.reduce((breakdown, rate) => {
    if (otherFieldTypeMapper[rate.type] && rate.amount > 0) {
      return { [otherFieldTypeMapper[rate.type]]: currencyFormatter(rate.amount) };
    }
    return breakdown;
  }, {});

const formatPremiumCoverage = (premiumCoverage) => {
  if (!premiumCoverage) {
    return 0;
  }
  return Object.entries(premiumCoverage).reduce(
    (acc, [key, coverage]) => ({ ...acc, [key]: currencyFormatter(coverage) }),
    {}
  );
};

// removes empty/false objects from the premium results
const removeEmptyValues = (premiumResults) => {
  if (!premiumResults) {
    return {};
  }
  return Object.fromEntries(Object.entries(premiumResults).filter(([, result]) => Boolean(result)));
};

const parsePremiumBreakdown = (premiumBreakdown, carCount) => {
  const retObj = {};
  for (let i = 1; i <= carCount; i += 1) {
    retObj[i] = Object.entries(premiumBreakdown)
      .filter(([key]) => key.toLowerCase().startsWith(`it${i}-`))
      .reduce((coverages, [key, value]) => {
        return coverages.concat({
          type: key.replace(/^it[0-9]-/i, '').toUpperCase(),
          amount: value
        });
      }, []);
  }
  const otherCoverages = Object.entries(premiumBreakdown)
    .filter(([key, value]) => Object.keys(otherFieldTypeMapper).includes(key) && value > 0)
    .reduce((acc, [key, value]) => {
      return acc.concat({ type: key, amount: value });
    }, []);
  const reducedCoverages = lodashGroupBy(otherCoverages, 'type');
  Object.keys(reducedCoverages).forEach((key) => {
    retObj[key] = reducedCoverages[key];
  });

  return retObj;
};

const useAutoPremiumBreakdown = (offer, policyType, premiumBreakdown, cars, mergedVehicles) =>
  useMemo(() => {
    const { state } = offer?.quote?.correctedAddress || {};
    const { autoCoverages } = offer?.options?.find((o) => o.type === getQuoteFriendlySelectedOption(policyType)) || {};

    const autoLimits = globalAutoLimits(state);
    const groupedByIteration = Object.keys(premiumBreakdown || {}).length
      ? parsePremiumBreakdown(premiumBreakdown, cars.length)
      : lodashGroupBy(autoCoverages || [], 'iteration');

    // Build set of UM/UIM coverage types for this state (used in legacy fallback)
    const stateUmUimTypes = umUimCoverageArray[state] || [];
    const umUimTypes = new Set([...stateUmUimTypes, 'UM_UIM']);

    // Determine whether UM/UIM types cause double-counting in legacy policies
    // (policies without PER_CAR_TOTAL stored in premiumBreakdown)
    let excludeUmUim = false;
    if (premiumBreakdown) {
      const carIterations = Object.entries(groupedByIteration).filter(([key]) => !isNaN(key));
      const hasPerCarTotal = carIterations.some(([, rates]) => rates.some((r) => r.type === 'PER_CAR_TOTAL'));
      if (!hasPerCarTotal && premiumBreakdown.car != null) {
        const totalAll = carIterations.reduce((sum, [, rates]) => sum + rates.reduce((s, r) => s + r.amount, 0), 0);
        excludeUmUim = totalAll > premiumBreakdown.car;
      }
    }

    const { premiumAutoCoverage, premiumCarsCoverages, premiumOtherCoverages } = Object.entries(
      groupedByIteration
    ).reduce(
      (acc, [key, rates]) => {
        const {
          premiumAutoCoverage: iterationAutoCoverage,
          premiumCarsCoverages: iterationCarCoverages,
          premiumOtherCoverages: iterationOtherCoverages
        } = acc;
        const vehicle = mergedVehicles[key - 1];

        const isCar = cars.some((c) => c.VIN === vehicle?.VIN);
        const isOtherCoverage = rates.some((r) => otherFieldTypeMapper[r.type] && r.amount > 0);

        if (!vehicle && !isOtherCoverage) {
          return {};
        }

        const otherPremium = isOtherCoverage && getOtherPremiumBreakdown(rates);
        const carPremiumBreakdown =
          isCar &&
          getCarPremiumBreakdown(
            rates,
            premiumBreakdown,
            {
              rideSharing: vehicle.rideSharing ? 'Included' : 'Not included'
            },
            { excludeUmUim, umUimTypes }
          );
        const premiumObjectKey = vehicle ? vehicle.VIN : key;
        return {
          premiumCarsCoverages: {
            ...iterationCarCoverages,
            [premiumObjectKey]: carPremiumBreakdown
          },
          premiumOtherCoverages: {
            ...iterationOtherCoverages,
            [premiumObjectKey]: otherPremium
          },
          premiumAutoCoverage: accumulatePremiumAutoCoverage(rates, autoLimits, iterationAutoCoverage)
        };
      },
      { premiumCarsCoverages: {}, premiumAutoCoverage: initialPremiumAutoCoverage, premiumOtherCoverages: {} }
    );
    return {
      premiumAutoCoverage: formatPremiumCoverage(premiumAutoCoverage),
      premiumCarsCoverages: removeEmptyValues(premiumCarsCoverages),
      premiumOtherCoverages: removeEmptyValues(premiumOtherCoverages)
    };
  }, [offer, policyType, premiumBreakdown, cars, mergedVehicles]);

export default useAutoPremiumBreakdown;
